import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { Card, CardBody, Col, Container, Row } from "reactstrap";
import { authKey_local_storage_key } from "../../../helpers/common";
import SmallLoader from "../../../helpers/Common/SmallLoader";

const PricingPage = () => {
  const authToken = localStorage.getItem(authKey_local_storage_key);
  const orders = useSelector((state) => state.orders);

  const plans = orders?.plans;
  const loading = orders?.plan_loading;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const PlanListing = () => {
    if (loading) {
      return <SmallLoader />;
    }

    const plansData = Array.isArray(plans)
      ? plans
      : Array.isArray(plans?.data)
      ? plans.data
      : [];

    if (plansData.length === 0) {
      return (
        <div className="text-center w-100 p-5">
          <h5 className="text-muted">No pricing packages available.</h5>
        </div>
      );
    }

    return (
      <Row className="g-4 justify-content-center align-items-stretch mt-2">
        {plansData.map((plan, key) => {
          // Check if plan is purchased from API response
          const isPurchased = Boolean(plan.isPurchased);

          const isPopular =
            plan.name?.toLowerCase() === "popular" ||
            plan.name?.toLowerCase() === "recommended";

          return (
            <Col lg={3} md={6} className="d-flex" key={plan.id || key}>
              <Card
                className={`pricing-card border-0 w-100 position-relative transition-all ${
                  isPopular ? "featured-popular-card shadow-lg" : "shadow-sm"
                }`}
                style={{ borderRadius: "20px" }}
              >
                {/* Highlight Badge for Popular Plan */}
                {isPopular && (
                  <div className="popular-badge text-center py-1 fw-bold text-uppercase fs-11">
                    Most Popular
                  </div>
                )}

                <CardBody className="p-4 d-flex flex-column justify-content-between position-relative z-1">
                  <div>
                    {/* Header: Plan Name & Subtitle */}
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h4 className="fw-bold text-dark mb-0">{plan.name}</h4>
                      {plan.totalCredits > 0 && (
                        <span className="badge bg-soft-primary text-primary rounded-pill px-2 py-1 fs-12">
                          {plan.totalCredits} Contacts
                        </span>
                      )}
                    </div>

                    <p
                      className="text-muted fs-13 mb-3 text-truncate"
                      title={plan.description}
                    >
                      {plan.description || "Ideal for hiring requirements"}
                    </p>

                    {/* Pricing Display */}
                    <div className="pricing-header py-3 mb-3 border-bottom border-top">
                      <div className="d-flex align-items-baseline">
                        <span className="fs-20 fw-bold me-1 text-primary">
                          ₹
                        </span>
                        <h1 className="fw-extrabold display-amount mb-0 text-dark me-2">
                          {parseInt(plan.planAmount || 0).toLocaleString(
                            "en-IN",
                          )}
                        </h1>
                        <span className="text-muted fs-13 fw-medium">
                          /{" "}
                          {plan.expiry ? `${plan.expiry} Days` : "Subscription"}
                        </span>
                      </div>
                      <span className="text-muted">+GST Excluded</span>
                    </div>

                    {/* Features List */}
                    <div className="feature-list mb-4">
                      <p className="fs-12 fw-bold text-uppercase tracking-wider text-muted mb-2">
                        Included Features:
                      </p>
                      <ul className="list-unstyled mb-0">
                        {(typeof plan?.features === "string"
                          ? plan.features.split("|")
                          : []
                        ).map((feature, index) => {
                          const trimmed = feature.trim();
                          if (!trimmed) return null;
                          return (
                            <li
                              key={index}
                              className="d-flex align-items-start mb-2 fs-14 text-secondary"
                            >
                              <svg
                                className="me-2 mt-1 flex-shrink-0"
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <circle
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  fill="#387ed8"
                                  fillOpacity="0.15"
                                />
                                <path
                                  d="M8.5 12.5L10.5 14.5L15.5 9.5"
                                  stroke="#387ed8"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                              <span>{trimmed}</span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </div>

                  {/* Action Button Link Context */}
                  <div className="mt-auto pt-2">
                    {isPurchased ? (
                      /* Purchased Plan: Current Active Plan Button (Disabled) */
                      <button
                        type="button"
                        className="btn btn-secondary-disabled rounded-pill w-100 fw-bold py-2 fs-13 text-uppercase"
                        disabled
                      >
                        <i className="mdi mdi-check-circle-outline me-1"></i>{" "}
                        Current Active Plan
                      </button>
                    ) : (
                      /* Unpurchased Plans: Active Select Button */
                      <Link
                        to={authToken ? `/checkout/${plan?.id}` : "/signin"}
                        className={`btn rounded-pill w-100 fw-bold py-2 fs-13 text-uppercase transition-all ${
                          isPopular
                            ? "btn-primary shadow-primary"
                            : "btn-outline-primary"
                        }`}
                      >
                        Select Plan
                      </Link>
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>
          );
        })}
      </Row>
    );
  };

  return (
    <React.Fragment>
      <section className="section bg-light py-5 mt-md-5 mt-4">
        <Container>
          <Row className="justify-content-center">
            <Col lg={7}>
              <div className="text-center mb-5">
                <span className="badge bg-soft-primary text-primary fs-12 mb-3 px-3 py-2 rounded-pill fw-bold tracking-wider text-uppercase">
                  Hiring Plans
                </span>
                <h2 className="fw-extrabold text-dark mb-3">
                  Flexible Hiring <span className="text-primary">Packages</span>
                </h2>
                <p className="text-muted fw-medium fs-15">
                  Choose a tailored plan to post jobs, access candidate
                  profiles, and hire top talent efficiently.
                </p>
              </div>
            </Col>
          </Row>

          <PlanListing />
        </Container>

        {/* Component Level Modern CSS Rules */}
        <style>{`
          .fw-extrabold { font-weight: 800; }
          .fs-11 { font-size: 11px; }
          .fs-12 { font-size: 12px; }
          .fs-13 { font-size: 13px; }
          .fs-14 { font-size: 14px; }
          .fs-20 { font-size: 20px; }
          .tracking-wider { letter-spacing: 1px; }

          .bg-soft-primary {
            background-color: rgba(56, 126, 216, 0.1) !important;
          }

          /* Pricing Card Customization */
          .pricing-card {
            background: #ffffff;
            border: 1px solid #e2e8f0 !important;
            transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
            overflow: hidden;
          }

          .pricing-card:hover {
            transform: translateY(-6px);
            border-color: #387ed8 !important;
            box-shadow: 0 15px 30px rgba(56, 126, 216, 0.12) !important;
          }

          /* Featured/Popular Plan Highlights */
          .featured-popular-card {
            border: 2px solid #387ed8 !important;
            transform: scale(1.02);
          }

          @media (max-width: 991px) {
            .featured-popular-card {
              transform: scale(1);
            }
          }

          .popular-badge {
            background: linear-gradient(135deg, #387ed8 0%, #1e40af 100%);
            color: #ffffff;
            letter-spacing: 1.5px;
          }

          .display-amount {
            font-size: 38px;
            letter-spacing: -1px;
            line-height: 1;
          }

          /* Metric Pills */
          .metric-pill {
            background-color: #f8fafc;
            border: 1px solid #e2e8f0;
            padding: 4px 10px;
            border-radius: 8px;
            font-size: 12px;
            font-weight: 600;
            color: #475569;
          }

          /* Button Styles */
          .shadow-primary {
            box-shadow: 0 6px 18px rgba(56, 126, 216, 0.35);
          }

          .btn-primary {
            background-color: #387ed8 !important;
            border-color: #387ed8 !important;
          }

          .btn-primary:hover {
            background-color: #2b6cb0 !important;
            border-color: #2b6cb0 !important;
          }

          .btn-outline-primary {
            color: #387ed8 !important;
            border-color: #387ed8 !important;
          }

          .btn-outline-primary:hover {
            background-color: #387ed8 !important;
            color: #ffffff !important;
          }

          /* Current Plan Disabled Button State */
          .btn-secondary-disabled {
            background-color: #f1f5f9 !important;
            color: #94a3b8 !important;
            border: 1px solid #cbd5e1 !important;
            cursor: not-allowed !important;
            pointer-events: auto !important;
          }
        `}</style>
      </section>
    </React.Fragment>
  );
};

export default PricingPage;